import React, { useState, useEffect } from 'react';
import useBuilderStore from '../store/useBuilderStore';
import { db } from '../../../Firebase';
import { collection, getDocs } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { HiOutlineCloudArrowUp, HiOutlineFolderOpen } from "react-icons/hi2";

const TemplateManager = () => {
  const { saveTemplate, loadTemplate } = useBuilderStore();
  const [templateName, setTemplateName] = useState('');
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchTemplates = async () => {
    try {
      const snapshot = await getDocs(collection(db, "emailTemplates"));
      setSavedTemplates(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleSave = async () => {
    if (!templateName.trim()) {
      toast.error("Enter a template name");
      return;
    }
    setIsLoading(true);
    try {
      await saveTemplate(templateName, db);
      toast.success("Design saved to cloud!");
      setTemplateName('');
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to save design");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Save Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-1">
          <HiOutlineCloudArrowUp className="w-4 h-4 text-[#8B3DFF]" />
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Save Design</label>
        </div>
        <div className="flex flex-col gap-2">
          <input
            type="text"
            placeholder="Template name..."
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            className="w-full px-4 py-2.5 text-xs border border-slate-100 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-[#8B3DFF]/20 focus:border-[#8B3DFF] outline-none transition-all placeholder:text-slate-300"
          />
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="w-full py-2.5 bg-gradient-to-r from-[#8B3DFF] to-[#7025e6] text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-xl hover:shadow-[0_8px_16px_rgba(139,61,255,0.2)] disabled:from-slate-200 disabled:to-slate-300 disabled:shadow-none transition-all active:scale-[0.98]"
          >
            {isLoading ? 'Uploading...' : 'Save to Library'}
          </button>
        </div>
      </div>

      {/* Load Section */}
      <div className="space-y-3 pt-4 border-t border-slate-50">
        <div className="flex items-center gap-2 mb-1">
          <HiOutlineFolderOpen className="w-4 h-4 text-[#00C4CC]" />
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Your Library</label>
        </div>
        <div className="grid grid-cols-1 gap-2.5 max-h-[250px] overflow-y-auto pr-2">
          {savedTemplates.map((tpl) => (
            <button
              key={tpl.id}
              onClick={() => {
                loadTemplate(tpl);
                toast.info(`Loaded: ${tpl.name}`);
              }}
              className="group relative text-left px-4 py-3 text-xs border border-slate-100 rounded-2xl bg-white hover:border-[#8B3DFF]/30 hover:shadow-[0_10px_20px_rgba(0,0,0,0.03)] transition-all flex justify-between items-center overflow-hidden"
            >
              <div className="flex flex-col gap-0.5">
                <span className="font-black text-slate-700 group-hover:text-[#8B3DFF] transition-colors">{tpl.name}</span>
                <span className="text-[9px] text-slate-300 font-bold uppercase">{new Date(tpl.createdAt).toLocaleDateString()}</span>
              </div>
              <div className="w-6 h-6 rounded-full bg-slate-50 group-hover:bg-purple-50 flex items-center justify-center transition-colors">
                 <svg className="w-3 h-3 text-slate-300 group-hover:text-[#8B3DFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M9 5l7 7-7 7" />
                 </svg>
              </div>
            </button>
          ))}
          {savedTemplates.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 bg-slate-50/50 rounded-2xl border border-dashed border-slate-100">
               <p className="text-[10px] text-slate-300 font-black uppercase tracking-tighter">Library is empty</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateManager;
